import { useCallback } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { hasPermission, type PermissionKey } from '@pcp/shared';
import { registerLockHandler, clearApiCache } from './authBridge';
import type { AuthStatus, AuthUser } from './api';

// Server is the source of truth for auth; this store mirrors it for snappy UX.
// `status` is never persisted — it always starts 'loading' and is set from
// GET /auth/state, so a tampered localStorage flag can never reveal the app.
export type GateStatus = AuthStatus | 'loading';

// Initial device-aware lock timeout used before the server tells us the exact
// value. Keep this regex in sync with apps/api/src/services/auth/session.ts
// (sessionTtlForUserAgent). Mobile 10 min, desktop 30 days.
const MOBILE_UA_RE = /Android|iPhone|iPad|iPod|Windows Phone|Mobile/i;
const detectLockAfterMs = (): number =>
  typeof navigator !== 'undefined' && MOBILE_UA_RE.test(navigator.userAgent)
    ? 10 * 60 * 1000
    : 30 * 24 * 60 * 60 * 1000;

interface AuthState {
  status: GateStatus;
  user: AuthUser | null;
  enrolled: boolean; // persisted hint: this browser has a passkey
  lastVisibleAt: number; // persisted: for the cold-start "closed >N min" check
  lockAfterMs: number; // persisted: device-aware lock timeout (server-confirmed)
  hydrate: (status: AuthStatus, user: AuthUser | null, lockAfterSeconds?: number) => void;
  lock: () => void;
  unlocked: (user: AuthUser | null, lockAfterSeconds?: number) => void;
  signedOut: () => void;
  markVisible: () => void;
  /** Refresh the user's effective permissions/roles in place (live role-edit pickup). */
  setUserPermissions: (permissions: string[], roles?: string[]) => void;
}

let channel: BroadcastChannel | null = null;
try {
  channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('pcp-auth') : null;
} catch {
  channel = null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      status: 'loading',
      user: null,
      enrolled: false,
      lastVisibleAt: Date.now(),
      lockAfterMs: detectLockAfterMs(),
      hydrate: (status, user, lockAfterSeconds) =>
        set({
          status,
          user,
          enrolled: get().enrolled || status === 'authenticated' || status === 'needs_unlock',
          ...(lockAfterSeconds ? { lockAfterMs: lockAfterSeconds * 1000 } : {}),
        }),
      lock: () =>
        set((s) => (s.status === 'authenticated' ? { status: 'needs_unlock' as GateStatus } : s)),
      unlocked: (user, lockAfterSeconds) =>
        set({
          status: 'authenticated',
          user,
          enrolled: true,
          lastVisibleAt: Date.now(),
          ...(lockAfterSeconds ? { lockAfterMs: lockAfterSeconds * 1000 } : {}),
        }),
      signedOut: () => set({ status: 'needs_entra', user: null }),
      markVisible: () => set({ lastVisibleAt: Date.now() }),
      setUserPermissions: (permissions, roles) =>
        set((s) =>
          s.user ? { user: { ...s.user, permissions, ...(roles ? { roles } : {}) } } : s,
        ),
    }),
    {
      name: 'pcp-auth',
      partialize: (s) => ({ enrolled: s.enrolled, lastVisibleAt: s.lastVisibleAt, lockAfterMs: s.lockAfterMs }),
    },
  ),
);

// ─── Wire the API 401 interceptor → lock, and broadcast across tabs ──
registerLockHandler(() => {
  useAuthStore.getState().lock();
  void clearApiCache();
  channel?.postMessage({ type: 'lock' });
});

if (channel) {
  channel.onmessage = (e: MessageEvent) => {
    const t = (e.data as { type?: string })?.type;
    if (t === 'lock') useAuthStore.getState().lock();
    else if (t === 'signedOut') useAuthStore.getState().signedOut();
    // 'unlock' isn't broadcast: each tab must present its own biometric.
  };
}

export function broadcastSignedOut(): void {
  channel?.postMessage({ type: 'signedOut' });
}

// ─── Permission checks (RBAC) ────────────────────────────────────
// UI GATING RULE: hide controls the user cannot use — never render them
// disabled. The server's 403 is the safety net. An array means ANY-OF.

/** Permissions that grant access to ANY part of the global Settings area. The
 *  Settings route + sidebar entry are shown only to users holding at least one
 *  of these; dashboard widgets that deep-link into Settings reuse this set so
 *  they hide the link (never disable it) for users who can't open Settings. */
export const SETTINGS_ACCESS_PERMS: PermissionKey[] = ['settings.manage', 'sync.run', 'maintenance.danger', 'users.manage'];

/** Per-tab Insights permissions. The Insights route + sidebar entry are shown
 *  only to users holding at least one of these (ANY-OF); the hub itself renders
 *  only the tabs the user can access. Admin holds all via the '*' wildcard. */
export const INSIGHTS_ACCESS_PERMS: PermissionKey[] = [
  'insights.overview', 'insights.tickets', 'insights.agents', 'insights.drafts',
  'insights.employees', 'insights.engineering', 'insights.community', 'insights.ai', 'insights.usage',
];

function checkPermission(
  status: GateStatus,
  permissions: readonly string[] | undefined,
  p: PermissionKey | PermissionKey[],
): boolean {
  // Local dev runs with auth off — everything is allowed.
  if (status === 'disabled') return true;
  const keys = Array.isArray(p) ? p : [p];
  return keys.some((key) => hasPermission(permissions, key));
}

/** Imperative permission check (event handlers, non-React code). Array = ANY-OF. */
export function can(p: PermissionKey | PermissionKey[]): boolean {
  const s = useAuthStore.getState();
  return checkPermission(s.status, s.user?.permissions, p);
}

/** Reactive permission check — re-renders when the user's permissions change. */
export function useCan(): (p: PermissionKey | PermissionKey[]) => boolean {
  const status = useAuthStore((s) => s.status);
  const permissions = useAuthStore((s) => s.user?.permissions);
  return useCallback(
    (p: PermissionKey | PermissionKey[]) => checkPermission(status, permissions, p),
    [status, permissions],
  );
}
